import { ConditionParam } from "@multiweb/clients/dataReader";
import { LazyLoadingSectionLogger, LoggingSection } from "@multiweb/logging";
import {
    IAttrValue,
    IOWClassAttributeDescription,
    IOWEnumAttributeDescription,
    IResultMessage,
    IRunMethodResult,
} from "@multiweb/types/contracts";
import { CancellationToken, RequestContext } from "@multiweb/types/multiweb";
import { ISignatureTemplate } from "Global/Base/Crypto/ISignatureInfo";
import { CaseInsensitiveMap } from "Global/Base/DataTypes/CaseInsensitiveMap";
import { Event } from "Global/Base/Events";
import { InvalidArgumentException } from "Global/Base/Exceptions";
import { UUID } from "Global/Base/IDs/UUID";
import { ValidationErrorType } from "OW.Remoting/Basic/EOWExceptionsInterfaces";
import { EOWRightEvaluationException } from "OW.Remoting/Basic/OWExceptions";
import {
    AttributeDescription,
    BasicDataType,
    IDataTypeSerialization,
    IOWClassDescription,
} from "OW.Remoting/Remoting/Cache/ClassDescriptionCache/Interfaces";
import { ModelDescriptionsManager } from "OW.Remoting/Remoting/Cache/ClassDescriptionCache/ModelDescriptionsManager";
import { IChangedAttr, IOWObject } from "OW.Remoting/Remoting/DataTypes/IOWObject";
import { IValidationDataSimple } from "OW.Remoting/Remoting/DataTypes/IValidationDataObject";
import { IWorkflowMenuItem } from "OW.Remoting/Remoting/IWorkflowManager";
import { OWClassDescription } from "OW.Remoting/Remoting/OWDescriptions";
import {
    IImplicitFormInfo,
    RunMethodBaseParams,
    RunMethodWithParamsBaseParams,
} from "OW.Remoting/Remoting/OWManagerInterfaces";
import { ClassItemRightTypes, ClassItemType, IClassItemRights } from "OW.Remoting/Remoting/Rights";
import { SerializeFuncs } from "OW.Remoting/Remoting/Serializers";
import { SerializerParams } from "OW.Remoting/Remoting/types";
import { ObjectContext } from "UI/UI/UI/UIManagerInterfaces";
import { convertContextFromFrameToRequest } from "UI/UI/Utilities/SharedUIMethods";
import { DateUtils } from "../../../../Global/Base/Utils/DateUtils";
import { AttrValueReader } from "../../../../OW.Remoting/Remoting/AttrValueReader";
import { Enum } from "../../../../OW.Remoting/Remoting/DataTypes/Enums";
import { NullOWObject } from "../../../../OW.Remoting/Remoting/DataTypes/OWObject";
import { OWManager } from "../../../../OW.Remoting/Remoting/OWManager";
import { MULTI_OBJECT_ID, MULTI_OBJECT_INSTANCE_PREFIX } from "./types";

const modelLogger = LazyLoadingSectionLogger.create(LoggingSection.modelDescription);

/**
 * Objekt reprezentující vícero záznamů
 * @internal
 */
export class MEOWObject implements IMultiEditObject {
    private _values: CaseInsensitiveMap<any> = new CaseInsensitiveMap<IAttrValue>();
    private _objCache: CaseInsensitiveMap<IOWObject> = new CaseInsensitiveMap<IOWObject>();
    private _validationErrors: IValidationDataSimple[] = [];

    /** Chain promisů s probihajicimi updaty dat */
    private _waitForUpdatesTask: Promise<void> = Promise.resolve();
    private _classInfo: OWClassDescription;
    /** Kolekce kompozitních atributů a jejich tříd */
    private readonly _composites: CaseInsensitiveMap<string>;

    public readonly objectName: string;
    public readonly objectIdentity: string | undefined = undefined;
    public readonly instanceID: string | undefined = undefined;
    public readonly id: any | undefined = MULTI_OBJECT_ID;
    public readonly isServerSideEncrypted: boolean | undefined = undefined;
    public readonly isEncryptionFinalized: boolean | undefined = undefined;
    public readonly isClientSideEncrypted: boolean | undefined = undefined;
    public canEdit: boolean | undefined = undefined;
    public canDelete: boolean | undefined = undefined;
    public canExecuteActions: boolean | undefined = undefined;
    public isNew: boolean = false;
    public isChanged: boolean = false;
    public isTempObject: boolean = false;
    public isDisposed: boolean = false;
    private _roleIds: any[];
    private _editRoleIds: any[];
    private _deleteRoleIds: any[];

    public get roleIds(): any[] {
        return this._roleIds;
    }
    public get editRoleIds(): any[] {
        return this._editRoleIds;
    }
    public get deleteRoleIds(): any[] {
        return this._deleteRoleIds;
    }
    public hasEncryptionPassword: boolean | undefined = undefined;
    public attributeOnParentObject: string | undefined = undefined;
    public isInstanceUpdateInProgress: boolean = false;
    public readonly valueSet: Event<IOWObject, IChangedAttr> = new Event<IOWObject, IChangedAttr>();
    public readonly attributeChanged: Event<IOWObject, string> = new Event<IOWObject, string>();
    public readonly attributesChanged: Event<IOWObject, string[]> = new Event<IOWObject, string[]>();
    public readonly newSaved: Event<IOWObject, void> = new Event<IOWObject, void>();
    public readonly beforeSave: Event<IOWObject, void> = new Event<IOWObject, void>();
    public readonly saved: Event<IOWObject, void> = new Event<IOWObject, void>();
    public readonly dataRefreshed: Event<IOWObject, boolean> = new Event<IOWObject, boolean>();
    public readonly objectDecrypted: Event<IOWObject, undefined> = new Event<IOWObject, undefined>();
    public readonly disposed: Event<IOWObject, undefined> = new Event<IOWObject, undefined>();
    public readonly deleted: Event<IOWObject, undefined> = new Event<IOWObject, undefined>();
    public readonly validityChange: Event<any, boolean> = new Event<IOWObject, boolean>();

    public get isMultiEditObject(): boolean {
        return true;
    }
    public get classInfo(): OWClassDescription {
        return this._classInfo;
    }

    //@ts-ignore zatím nepodporujeme hromadnou editaci v rámci editačního kontextu
    private readonly _context: ObjectContext;

    private constructor(context: ObjectContext, objectName: string, composites: CaseInsensitiveMap<string>) {
        this._context = context;
        this.objectName = objectName;
        this._composites = composites;
        this.instanceID = MULTI_OBJECT_INSTANCE_PREFIX + UUID.generate();
    }

    private async _init(): Promise<void> {
        //TODO: šlo by přesunout do RM? a mělo by to význam? c1: možná to tady bude výhodnější, volá se to při každé změně výběru?? pokud ano, tak se musí dát jinam, nebo kešovat
        this._classInfo = await ModelDescriptionsManager.instance.getClassInfo(this.objectName) as OWClassDescription;
        //TODO: kešovat, asi nemá smysl ptát se to pořád dokola, když objectName se na framu nemění.
        const firmlyDisabledRights = await OWManager.firmlyDisabledRights(this.objectName, this._getRequestContext());
        const classInterfaceManipulativeRoles = await OWManager.classInterfaceManipulativeRoles(this.objectName, this._getRequestContext());
        this._roleIds = classInterfaceManipulativeRoles.roleIds;
        this._editRoleIds = classInterfaceManipulativeRoles.editRoleIds;
        this._deleteRoleIds = classInterfaceManipulativeRoles.deleteRoleIds;
        this.canEdit = !firmlyDisabledRights.edit;
        this.canDelete = !firmlyDisabledRights.delete;
        this.canExecuteActions = true; //TODO: tohle taky za normálních okolností chodí v owobjectu, co s tím? Napevno true?
    }

    private _getRequestContext(cancellationToken: CancellationToken = CancellationToken.none): RequestContext {
        return convertContextFromFrameToRequest(this._context, cancellationToken);
    }

    public static async createObject(context: ObjectContext, objectName: string, composites: CaseInsensitiveMap<string>): Promise<IOWObject> {
        const obj = new MEOWObject(context, objectName, composites);
        await obj._init();
        return obj;
    }

    public async getSerializers(ai: AttributeDescription, params?: SerializerParams): Promise<IDataTypeSerialization> {
        return SerializeFuncs.getTypeSerializers(ai.type, (ai as IOWEnumAttributeDescription).enumName, params);
    }
    public getEncryptionKey(_cancellationToken?: CancellationToken): Promise<CryptoKey> {
        throw new Error("Method not implemented.");
    }
    public refresh(): Promise<void> {
        throw new Error("Method not implemented.");
    }
    public reload(): Promise<void> {
        return Promise.resolve();
    }
    public waitForPendingUpdates(): Promise<void> {
        return this._waitForUpdatesTask;
    }
    public enqueuePendingUpdateTask(task: Promise<void>): Promise<void> {
        return this._waitForUpdatesTask = this._waitForUpdatesTask.then(() => task);
    }
    public decrypt(): Promise<boolean> {
        throw new Error("Method not implemented.");
    }

    public async getObjectAttributeClass(attributeName: string, cancellationToken: CancellationToken = CancellationToken.none): Promise<string> {
        try {
            const attrInfo = await MEOWObject.getAttributeInfo(this._classInfo, attributeName, this._composites, cancellationToken);

            if (attrInfo.basicType === BasicDataType.OWObject) {
                return (attrInfo as IOWClassAttributeDescription).className;
            }
        } catch (e) {
            // Vyjimky jsou ignorovany, atributy, ktere nebyly nalezeny nemuseli byt nalezeny z duvodu pretypovani, ktere neni mozne zjistit
        }

        return null;
    }
    public async getAttributeMetadata(attributePath: string, cancellationToken: CancellationToken = CancellationToken.none): Promise<AttributeDescription | null> {
        try {
            return await MEOWObject.getAttributeInfo(this._classInfo, attributePath, this._composites, cancellationToken);
        } catch (e) {
            // Vyjimky jsou ignorovany, atributy, ktere nebyly nalezeny nemuseli byt nalezeny z duvodu pretypovani, ktere neni mozne zjistit
        }
        return null;
    }

    /**
     * Vrátí informace o atributu dané třídy
     * @param startClassInfo Předá ClassDescription nebo název třídy
     * @param attributePath Kompletní cesta k atributu, vč. přetypování (a.b.c|d.kod)
     * @param cancellationToken
     */
    public static async getAttributeInfo(startClassInfo: IOWClassDescription | string, attributePath: string, composites: CaseInsensitiveMap<string>, cancellationToken: CancellationToken = CancellationToken.none): Promise<AttributeDescription> {
        let attrInfo: AttributeDescription = null;
        let classInfo: IOWClassDescription = null;

        if (!startClassInfo) throw new InvalidArgumentException("startClassInfo can't be null");
        if (typeof startClassInfo === "string") {
            classInfo = await ModelDescriptionsManager.instance.getClassInfo(startClassInfo) as IOWClassDescription;
        } else classInfo = startClassInfo;

        const firstClassInfo = classInfo;
        const attrsList = attributePath.includes(".") ? attributePath.split(".").reverse() : [ attributePath ];

        while (attrsList.length > 0) {
            const attrNameFull = attrsList.pop();
            const [ attrName, typeCastClassName ] = attrNameFull.split("|");

            if (typeCastClassName != null) { // pri pretypovani musime nacist informace z jine tridy
                classInfo = await ModelDescriptionsManager.instance.getClassInfo(typeCastClassName, cancellationToken);
            }

            try {
                attrInfo = await classInfo.getAttributeInfo(attrName);
            } catch (e) {
                attrInfo = null;
            }

            if (!attrInfo) {
                modelLogger.warn(`Attribute path "${attributePath}" on "${firstClassInfo.className}" not found!`);
                return null;//throw new EOWException(`Attribute path "${attributePath}" on "${firstClassInfo.className}" not found!`); //`
            }

            if (attrInfo.basicType === BasicDataType.OWObject) {
                const className = composites.get(attrName) ?? (attrInfo as IOWClassAttributeDescription).className;
                classInfo = await ModelDescriptionsManager.instance.getClassInfo(className, cancellationToken);
            } else break;
        }

        if (attrsList.length > 0) {
            //throw new EOWException(`Attribute path "${attributePath}" on "${firstClassInfo.className}" not found!`);
            modelLogger.warn(`Attribute path "${attributePath}" on "${firstClassInfo.className}" not found!`);
            return null;
        }

        return attrInfo;
    }
    public validate(): Promise<void> {
        throw new Error("Method not implemented.");
    }
    public postponedValidate(): Promise<void> {
        throw new Error("Method not implemented.");
    }
    public async getValue(attributeName: string): Promise<IAttrValue> {
        let value = this._values.get(attributeName);
        const attrInfo = await MEOWObject.getAttributeInfo(this.classInfo, attributeName, this._composites, CancellationToken.none);
        if (attrInfo?.basicType === BasicDataType.OWObject) {
            if (!(value == null || value instanceof IOWObject)) {
                let cacheValue = this._objCache.get(attributeName);

                if (cacheValue?.id !== value) cacheValue = null;
                if (cacheValue) value = cacheValue;
                else {
                    const obj = await OWManager.getObject(value, (attrInfo as IOWClassAttributeDescription).className);
                    this._objCache.set(attributeName, obj);

                    value = obj;
                }
            }
        }

        return value;
    }
    public getValues(attrNames: string[]): Promise<IAttrValue[]> {
        return Promise.resolve(attrNames.filter(a => this._values.has(a)).map(i => this._values.get(i) as IAttrValue));
    }
    public async getParameters(attrNames: readonly string[]): Promise<readonly ConditionParam[]> {
        if (attrNames == null)
            return null;

        const promises = new Array<Promise<ConditionParam>>();
        for (const attr of attrNames) {
            const prom = this.getValue(attr).then((value) => {
                value = AttrValueReader.read(value);

                if (value instanceof Date) {
                    return DateUtils.toXsdDateTime(value);
                }
                if (value instanceof Enum) {
                    return Number(value);
                }
                if (value instanceof NullOWObject)
                    return null;

                if (value instanceof IOWObject)
                    return value.id;

                return value;
            });
            promises.push(prom);
        }

        return Promise.all(promises);
    }
    public async waitForUpdateInstance(_cancellationToken?: CancellationToken): Promise<void> {
        // při hromadné editaci se updaty atributů neposílají samostatně, tedy není potřeba na jejich doběhnutí čekat
    }
    public setValue(attributeName: string, value: any): Promise<IOWObject> {
        this._values.set(attributeName, value);
        this.valueSet.trigger(this, { attributeName, value });
        this.isChanged = true;
        return Promise.resolve(this);
    }
    public undo(): Promise<void> {
        throw new Error("Method not implemented.");
    }
    public redo(): Promise<void> {
        throw new Error("Method not implemented.");
    }
    public getLinkNamesForInstance(_instanceID: string): Promise<string[]> {
        return Promise.resolve([]);
    }
    public delete(): Promise<IResultMessage[]> {
        throw new Error("Method not implemented.");
    }
    public applyUpdates(): Promise<IResultMessage[]> {
        throw new Error("Method not implemented.");
    }
    public itemRights(itemName: string, type: ClassItemType, evalRights: ClassItemRightTypes, cancellationToken: CancellationToken = CancellationToken.none): Promise<IClassItemRights> {
        //TODO: udělat kešování
        //TODO: doplnit aktivní role do requestu
        if (!itemName) {
            throw new EOWRightEvaluationException("", "Unable to evaluate rights for attribute. Attribute name is empty!");
        }

        return OWManager.classItemsRights(this.classInfo.className, this._applyCompositeCastings(itemName), type, evalRights, this._getRequestContext(cancellationToken)).then((result) => {
            if (result.ExceptionText)
                throw new EOWRightEvaluationException(itemName, result.ExceptionText, "", this.objectName);
            return result;
        });
    }

    private _applyCompositeCastings(fullAttrName: string): string {
        if (!fullAttrName.includes("."))
            return fullAttrName;

        let result = "";
        let rest = fullAttrName;

        do {
            const attrName = rest.substring(0, rest.indexOf("."));
            rest = rest.substring(rest.indexOf(".") + 1, rest.length);
            if (this._composites.has(attrName))
                result = result.concat(`${attrName}|${this._composites.get(attrName)}.`);
            else
                result = result.concat(`${attrName}.`);
        } while (rest.includes("."));

        return result.concat(rest);
    }

    public evaluateCondition(_conditionID: string): Promise<boolean> {
        return Promise.resolve(true); //TODO-rites: je to ok? Prověřit
    }
    public cancelUpdatesInProgress(): void {
        throw new Error("Method not implemented.");
    }
    public run(_config: RunMethodBaseParams): Promise<IRunMethodResult> {
        throw new Error("Method not implemented.");
    }
    public runWithParams(_config: RunMethodWithParamsBaseParams): Promise<IRunMethodResult> {
        throw new Error("Method not implemented.");
    }
    public mapSignDataToTemplate(_template: ISignatureTemplate, _serializationFormat: string): Promise<object> {
        throw new Error("Method not implemented.");
    }
    public wfMenu(_sort?: boolean): Promise<IWorkflowMenuItem[]> {
        throw new Error("Method not implemented.");
    }
    public createCopy(): Promise<IOWObject> {
        throw new Error("Method not implemented.");
    }
    public hasWf(): Promise<boolean> {
        return Promise.resolve(false);
    }
    public getObjectAttributeID(_attributeName: string, _cancellationToken?: CancellationToken): Promise<string> {
        throw new Error("Method not implemented.");
    }
    public getImplicitForm(_attrName?: string, _conditionParameters?: string): Promise<IImplicitFormInfo> {
        throw new Error("Method not implemented.");
    }
    public isAttributeReadOnly(_attrName: string): Promise<boolean> {
        return Promise.resolve(false);
    }
    public isAttributeEncrypted(_attrName: string): Promise<boolean> {
        return Promise.resolve(false);
    }
    public createSignature(_signature: string, _signedData: string, _profileId: string, _providerType: string, _providerVersion: string,
        _signatureSourcesEncrypted?: boolean): Promise<string> {
        throw new Error("Method not implemented.");
    }
    public get isValid(): boolean {
        return this._validationErrors.length === 0;
    }

    public getValidationErrors(): IValidationDataSimple[] {
        return this._validationErrors.slice(); // shallow copy
    }
    /**
     * Přidá novou validační chybu
     * @param error Informace o validační chybě
     */
    public addError(error: IValidationDataSimple): void {
        const isFirstValidationError = error.error.type !== ValidationErrorType.recommendedError && !this._validationErrors.some((e) => e.error.type !== ValidationErrorType.recommendedError);

        this._validationErrors.push(error);
        //prvni validacni chyba, ktera znemoznuje ulozeni (nepatri zde recommended)
        if (isFirstValidationError)
            this.validityChange.trigger(this, false);
    }

    /**
     * Odstraní validační chybu
     * @param error chyba k odstranění
     */
    public removeError(error: IValidationDataSimple): void {
        const pos = this._validationErrors.findIndex((e) => e === error);
        if (pos < 0)
            return;

        this._validationErrors.splice(pos, 1);
        if (this.isValid)
            this.validityChange.trigger(this, true);
    }

    /**
     * Odstraní všechny validační chyby
     */
    public clearErrors(): void {
        const isValid = this.isValid;
        this._validationErrors = [];
        if (!isValid)
            this.validityChange.trigger(this, true);
    }

    public valueOf(): any {
        throw new Error("Method not implemented.");
    }
    public toString(): string {
        throw new Error("Method not implemented.");
    }
    public async dispose(): Promise<void> {
        for (const obj of this._objCache.values()) {
            obj.dispose();
        }
    }
    public compare(_value: any): boolean {
        throw new Error("Method not implemented.");
    }
}

/**
 * Zástupný objekt, který reprezentuje vícero objektů současně (třeba při hromadném výběru)
 * @includeToDoc
 */
export interface IMultiEditObject extends IOWObject {
    /** Příznak, jestli se jedná o objekt zastupující skupinu objektů */
    readonly isMultiEditObject: boolean;
}

export class IMultiEditObject {
    private static _properties: (keyof IMultiEditObject)[] = ["isMultiEditObject"];

    public static [Symbol.hasInstance](instance: any): boolean {
        return instance != null && typeof instance === "object" && !this._properties.some((value) => !Reflect.has(instance, value))
            && instance instanceof IOWObject;
    }
}
